// Sentry (errors + 10 % performance traces). No-op without REACT_APP_SENTRY_DSN. No session replay. Nothing personal is sent:
// bodies, cookies, headers, query strings, passwords / PINs / tokens, phones, e-mails and names are scrubbed; only the user's ROLE is tagged.
import * as Sentry from "@sentry/react";

const DSN = (process.env.REACT_APP_SENTRY_DSN || "").trim();
const RELEASE = globalThis.__SENTRY_RELEASE__ || undefined;  // injected at build time by craco (DefinePlugin)
const KEY = /pass(word|code)?|pwd|pin|token|secret|auth|cookie|session|otp|api[_-]?key|dsn|phone|mobile|whatsapp|e-?mail|name|username|guardian|address|birth|iban|card/i;
const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE = /\+?\d[\d\s().-]{6,}\d/g;
const TOKENISH = /\b(eyJ[\w-]+\.[\w-]+\.[\w-]+|[A-Za-z0-9_-]{24,})\b/g;
const F = "[Filtered]";
const str = (s) => s.replace(TOKENISH, F).replace(EMAIL, F).replace(PHONE, F);
const IDS = new Set(["trace_id", "span_id", "parent_span_id", "event_id", "release"]);  // Sentry's own ids stay intact (trace linking)
const SAFE_KEYS = new Set(["type", "transaction", "module", "filename", "function", "abs_path"]);

function scrub(v, depth = 0) {
  if (depth > 12) return F;
  if (typeof v === "string") return str(v);
  if (Array.isArray(v)) return v.map((x) => scrub(x, depth + 1));
  if (v && typeof v === "object") {
    const o = {};
    for (const [k, x] of Object.entries(v)) o[k] = IDS.has(k) ? x : KEY.test(k) && !SAFE_KEYS.has(k) ? F : scrub(x, depth + 1);
    return o;
  }
  return v;
}

function role() {  // from the JWT payload — the role only, never id / name / e-mail
  try { const t = localStorage.getItem("cs:token"); return t ? JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).role || "unknown" : "anon"; } catch { return "unknown"; }
}

export function scrubEvent(event) {
  if (event.request) {
    delete event.request.data; delete event.request.cookies; delete event.request.headers; delete event.request.query_string;
    if (event.request.url) event.request.url = str(event.request.url.split("?")[0]);
  }
  delete event.user;
  for (const k of ["extra", "contexts", "breadcrumbs", "exception", "message", "spans", "transaction"]) if (event[k]) event[k] = scrub(event[k]);
  event.tags = { ...scrub(event.tags || {}), role: role() };
  return event;
}

const host = typeof window !== "undefined" ? window.location.hostname : "";
export const SENTRY_ON = !!DSN;
if (DSN) {
  Sentry.init({
    dsn: DSN,
    release: RELEASE,
    environment: /(^|\.)buma\.academy$/.test(host) ? "production" : "preview",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    tracePropagationTargets: [],  // no tracing headers to the API (keeps CORS untouched)
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: (b) => (b.category === "console" ? null : scrub(b)),
  });
}

export { Sentry };
